import React, { createContext, useState, useContext, ReactNode, useEffect, useRef } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';

const API_BASE = process.env.REACT_APP_API_URL ?? '';
const API_URL = `${API_BASE}/api`;

export interface DocumentShortcut {
  id: string;
  documentId?: string;
  documentTypeId?: string; // Document type _id for navigation
  documentNumber?: string;
  documentCode: string;
  documentName: string;
  category: string;
  subCategory?: string;
  subType?: string;
  path: string;
  timestamp: number;
}

interface ShortcutContextType {
  shortcuts: DocumentShortcut[];
  addShortcut: (shortcut: Omit<DocumentShortcut, 'id' | 'timestamp'>) => void;
  removeShortcut: (id: string) => void;
  clearAllShortcuts: () => void;
  hasShortcut: (path: string) => boolean;
  reorderShortcuts: (fromIndex: number, toIndex: number) => void;
}

const ShortcutContext = createContext<ShortcutContextType>({
  shortcuts: [],
  addShortcut: () => {},
  removeShortcut: () => {},
  clearAllShortcuts: () => {},
  hasShortcut: () => false,
  reorderShortcuts: () => {},
});

export const useShortcut = () => useContext(ShortcutContext);

interface ShortcutProviderProps {
  children: ReactNode;
}

export const ShortcutProvider: React.FC<ShortcutProviderProps> = ({ children }) => {
  const { user } = useAuth();
  const [shortcuts, setShortcuts] = useState<DocumentShortcut[]>([]);

  // Track the user id that triggered the last load so we can guard stale responses.
  const loadedForUserRef = useRef<string | null>(null);

  // Persist the current shortcuts array to the server (fire-and-forget).
  const persist = (next: DocumentShortcut[]) => {
    const token = localStorage.getItem('token');
    if (!token) return;
    axios
      .put(
        `${API_URL}/users/me/shortcuts`,
        { shortcuts: next },
        { headers: { 'x-auth-token': token } }
      )
      .catch(console.warn);
  };

  // Load shortcuts from the server when a user logs in.
  useEffect(() => {
    if (!user?._id) {
      // Logout — clear state only; server data is preserved.
      loadedForUserRef.current = null;
      setShortcuts([]);
      return;
    }

    const userId = user._id;
    const token = localStorage.getItem('token');
    if (!token) return;

    loadedForUserRef.current = userId;
    const localKey = `shortcuts_${userId}`;

    axios
      .get<{ shortcuts: DocumentShortcut[] }>(`${API_URL}/users/me/shortcuts`, {
        headers: { 'x-auth-token': token },
      })
      .then((res) => {
        // Guard against stale response arriving after logout / user switch.
        if (loadedForUserRef.current !== userId) {
          return;
        }

        const serverShortcuts = Array.isArray(res.data?.shortcuts)
          ? res.data.shortcuts
          : [];

        if (serverShortcuts.length > 0) {
          // Server has data — use it and clean up any stale localStorage copy.
          setShortcuts(serverShortcuts);
          localStorage.removeItem(localKey);
        } else {
          // Server is empty — one-time migration from localStorage.
          const raw = localStorage.getItem(localKey);
          let local: DocumentShortcut[] = [];
          if (raw) {
            try {
              const parsed = JSON.parse(raw);
              local = Array.isArray(parsed) ? parsed : [];
            } catch {
              local = [];
            }
          }

          if (local.length > 0) {
            // Migrate: push local data to server, then remove the local copy.
            setShortcuts(local);
            axios
              .put(
                `${API_URL}/users/me/shortcuts`,
                { shortcuts: local },
                { headers: { 'x-auth-token': token } }
              )
              .then(() => localStorage.removeItem(localKey))
              .catch(console.warn);
          } else {
            setShortcuts([]);
          }
        }
      })
      .catch((err) => {
        // Network or server error — fall back to localStorage if available.
        console.warn('ShortcutContext: failed to load shortcuts from server, falling back to localStorage', err);
        if (loadedForUserRef.current !== userId) {
          return;
        }

        const raw = localStorage.getItem(localKey);
        if (raw) {
          try {
            const parsed = JSON.parse(raw);
            setShortcuts(Array.isArray(parsed) ? parsed : []);
          } catch {
            setShortcuts([]);
          }
        } else {
          setShortcuts([]);
        }
      });
  }, [user?._id]); // eslint-disable-line react-hooks/exhaustive-deps

  const addShortcut = (shortcut: Omit<DocumentShortcut, 'id' | 'timestamp'>) => {
    // Check for duplicates by path — each unique URL path can only have one shortcut.
    if (shortcuts.some((s) => s.path === shortcut.path)) {
      return;
    }

    const newShortcut: DocumentShortcut = {
      ...shortcut,
      id: `${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      timestamp: Date.now(),
    };

    const next = [...shortcuts, newShortcut];
    setShortcuts(next);
    persist(next);
  };

  const removeShortcut = (id: string) => {
    const next = shortcuts.filter((s) => s.id !== id);
    setShortcuts(next);
    persist(next);
  };

  const clearAllShortcuts = () => {
    const next: DocumentShortcut[] = [];
    setShortcuts(next);
    persist(next);
  };

  const hasShortcut = (path: string): boolean => {
    return shortcuts.some((s) => s.path === path);
  };

  const reorderShortcuts = (fromIndex: number, toIndex: number) => {
    if (
      fromIndex === toIndex ||
      fromIndex < 0 || fromIndex >= shortcuts.length ||
      toIndex < 0 || toIndex >= shortcuts.length
    ) {
      return;
    }
    const next = [...shortcuts];
    const [moved] = next.splice(fromIndex, 1);
    next.splice(toIndex, 0, moved);
    setShortcuts(next);
    persist(next);
  };

  return (
    <ShortcutContext.Provider
      value={{
        shortcuts,
        addShortcut,
        removeShortcut,
        clearAllShortcuts,
        hasShortcut,
        reorderShortcuts,
      }}
    >
      {children}
    </ShortcutContext.Provider>
  );
};
